import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Bank reconciliation — FR-ACC-05
 * Import statement, auto-match, manually reconcile unmatched lines.
 * Phase B styling: treasury workflow for the auditor persona.
 */
export function BankReconciliationPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const listQuery = useQuery({
    queryKey: ['bank-statements'],
    queryFn: () => accountingApi.listBankStatements(),
  });

  const kpis = useMemo(() => {
    const rows = listQuery.data ?? [];
    let inProgress = 0, reconciled = 0, closed = 0, unmatchedLines = 0;
    for (const s of rows) {
      if (s.status === 'in_progress') inProgress++;
      else if (s.status === 'reconciled') reconciled++;
      else if (s.status === 'closed') closed++;
      unmatchedLines += Math.max(0, (s.line_count ?? 0) - (s.matched_count ?? 0));
    }
    return { total: rows.length, inProgress, reconciled, closed, unmatchedLines };
  }, [listQuery.data]);

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Treasury"
        title="Bank reconciliation"
        subtitle="Import bank statements, auto-match against journal entries, and manually reconcile the rest."
        actions={
          <button className="btn-primary" onClick={() => setShowImport(true)}>
            + Import statement
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Statements" value={kpis.total} />
        <KpiTile label="In progress" value={kpis.inProgress}
          accent={kpis.inProgress > 0 ? 'amber' : 'default'} />
        <KpiTile label="Reconciled" value={kpis.reconciled} accent="sage" />
        <KpiTile label="Unmatched lines" value={kpis.unmatchedLines}
          accent={kpis.unmatchedLines > 0 ? 'amber' : 'default'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_40rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !listQuery.data?.length ? (
            <EmptyState
              message="No bank statements imported yet"
              hint="Paste your first bank CSV to begin reconciliation."
              action={
                <button className="btn-primary" onClick={() => setShowImport(true)}>
                  + Import statement
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Stmt #</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Matched</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(listQuery.data ?? []).map((s: any, i: number) => {
                    const pct = s.line_count > 0 ? (s.matched_count / s.line_count) * 100 : 0;
                    return (
                      <tr
                        key={s.id}
                        onClick={() => setSelected(s.id)}
                        className={clsx(
                          'cursor-pointer transition-colors',
                          selected === s.id
                            ? 'bg-finance-50'
                            : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                        )}
                      >
                        <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">
                          {s.statement_number ?? <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-mono text-xs font-semibold text-slate-900">{s.account_code}</div>
                          <div className="text-2xs text-slate-500 mt-0.5">{s.account_name}</div>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600 tabular-nums">
                          {new Date(s.statement_date).toLocaleDateString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </td>
                        <td className="px-4 py-2"><StatementStatusBadge status={s.status} /></td>
                        <td className="px-4 py-2 text-right">
                          <div className="font-mono font-semibold text-slate-900 tabular-nums">
                            {s.matched_count}/{s.line_count}
                          </div>
                          {s.line_count > 0 && (
                            <div className="mt-1 w-20 h-1 rounded-full bg-slate-100 overflow-hidden ml-auto">
                              <div
                                className={clsx(
                                  'h-full transition-all',
                                  pct === 100 ? 'bg-sage-500' : pct > 50 ? 'bg-finance-500' : 'bg-amber-500'
                                )}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[500px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select a statement"
              hint="Click a statement on the left to view lines and reconcile."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <line x1="2" y1="10" x2="22" y2="10"/>
                  <line x1="6" y1="15" x2="10" y2="15"/>
                </svg>
              }
            />
          ) : (
            <StatementDetail
              statementId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['bank-statements'] })}
            />
          )}
        </div>
      </div>

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={(id) => {
            qc.invalidateQueries({ queryKey: ['bank-statements'] });
            setShowImport(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function StatementStatusBadge({ status }: { status: string }) {
  const cls = {
    pending:      'bg-slate-100 text-slate-700 ring-slate-200',
    in_progress:  'bg-amber-50 text-amber-800 ring-amber-200',
    reconciled:   'bg-sage-50 text-sage-700 ring-sage-200',
    closed:       'bg-finance-50 text-finance-800 ring-finance-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ============================================================
// StatementDetail — the reconciliation workspace
// ============================================================
function StatementDetail({ statementId, onChanged }: { statementId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [matchingLineId, setMatchingLineId] = useState<string | null>(null);
  const [ignoringLineId, setIgnoringLineId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const q = useQuery({
    queryKey: ['bank-statement', statementId],
    queryFn: () => accountingApi.getBankStatement(statementId),
  });

  const autoMatchMut = useMutation({
    mutationFn: () => accountingApi.autoMatchStatement(statementId),
    onSuccess: (d) => {
      toast.success(
        `Auto-matched ${d.matched} new · ${d.totalMatched}/${d.totalLines} total`,
        { duration: 6000 }
      );
      qc.invalidateQueries({ queryKey: ['bank-statement', statementId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const unmatchMut = useMutation({
    mutationFn: (lineId: string) => accountingApi.unmatchLine(lineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-statement', statementId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const ignoreMut = useMutation({
    mutationFn: ({ lineId, reason }: { lineId: string; reason: string }) =>
      accountingApi.ignoreLine(lineId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-statement', statementId] });
      onChanged();
      setIgnoringLineId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const closeMut = useMutation({
    mutationFn: () => accountingApi.closeBankStatement(statementId),
    onSuccess: (d) => {
      toast.success(d.fullyBalanced ? '✓ Statement closed, fully balanced' : '⚠ Closed with unbalanced matching sum');
      qc.invalidateQueries({ queryKey: ['bank-statement', statementId] });
      onChanged();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={5} />;
  if (q.error) return <ErrorState error={q.error} />;
  const s = q.data;

  const unmatchedCount = s.lines.filter((l: any) => l.match_status === 'unmatched').length;
  const ignoredCount = s.lines.filter((l: any) => l.match_status === 'ignored').length;
  const matchedCount = s.lines.filter((l: any) => ['auto_matched', 'manual_matched'].includes(l.match_status)).length;
  const isLocked = s.status === 'closed';

  const FILTERS = [
    { value: '',                label: 'All',       count: s.lines.length },
    { value: 'unmatched',       label: 'Unmatched', count: unmatchedCount },
    { value: 'auto_matched',    label: 'Auto',      count: s.lines.filter((l: any) => l.match_status === 'auto_matched').length },
    { value: 'manual_matched',  label: 'Manual',    count: s.lines.filter((l: any) => l.match_status === 'manual_matched').length },
    { value: 'ignored',         label: 'Ignored',   count: ignoredCount },
  ];

  const filteredLines = statusFilter ? s.lines.filter((l: any) => l.match_status === statusFilter) : s.lines;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="eyebrow text-finance-700 mb-1">Bank statement</div>
          <div className="font-mono font-bold text-lg text-slate-900">{s.statement_number}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-xs font-semibold text-slate-900">{s.account_code}</span>
            <span className="text-xs text-slate-500">{s.account_name}</span>
          </div>
          <div className="font-mono text-xs text-slate-500 mt-1">
            {new Date(s.statement_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <StatementStatusBadge status={s.status} />
      </div>

      {/* Balance summary + progress */}
      <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <div className="eyebrow text-slate-500">Opening balance</div>
            <div className="font-mono font-bold text-xl text-slate-900 tabular-nums mt-1">
              ${parseFloat(s.opening_balance).toFixed(2)}
            </div>
          </div>
          <div className="text-right">
            <div className="eyebrow text-slate-500">Closing balance</div>
            <div className="font-display font-bold text-2xl text-slate-900 tabular-nums tracking-tight mt-0.5">
              ${parseFloat(s.closing_balance).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Match progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="eyebrow text-slate-500">Match progress</span>
            <span className="font-mono text-xs font-semibold text-slate-700 tabular-nums">
              {matchedCount + ignoredCount} / {s.line_count}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden flex">
            {s.line_count > 0 && (
              <>
                <div className="h-full bg-sage-500 transition-all" style={{ width: `${(matchedCount / s.line_count) * 100}%` }} />
                <div className="h-full bg-slate-400 transition-all" style={{ width: `${(ignoredCount / s.line_count) * 100}%` }} />
              </>
            )}
          </div>
          <div className="flex gap-3 mt-2 text-2xs text-slate-600">
            <LegendDot color="bg-sage-500" label="Matched" count={matchedCount} />
            <LegendDot color="bg-slate-400" label="Ignored" count={ignoredCount} />
            <LegendDot color="bg-slate-200" label="Unmatched" count={unmatchedCount} />
          </div>
        </div>

        {s.reconciled_at && (
          <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600">
            Closed {new Date(s.reconciled_at).toLocaleString()}
            {s.reconciled_by_name && ` · ${s.reconciled_by_name}`}
          </div>
        )}
      </div>

      {/* Action row */}
      {!isLocked && (
        <div className="flex gap-2 items-center mb-4 flex-wrap">
          {unmatchedCount > 0 && (
            <button
              className="btn-secondary"
              disabled={autoMatchMut.isPending}
              onClick={() => autoMatchMut.mutate()}
            >
              {autoMatchMut.isPending ? 'Matching…' : `Auto-match ${unmatchedCount}`}
            </button>
          )}
          <div className="flex-1" />
          <button
            className={clsx(
              'btn-primary',
              unmatchedCount > 0 && 'opacity-50 cursor-not-allowed'
            )}
            disabled={unmatchedCount > 0 || closeMut.isPending}
            onClick={() => closeMut.mutate()}
            title={unmatchedCount > 0 ? `${unmatchedCount} unmatched line${unmatchedCount === 1 ? '' : 's'} remaining — match or ignore first` : undefined}
          >
            {closeMut.isPending ? 'Closing…' : 'Close statement'}
          </button>
        </div>
      )}

      {/* Line filters */}
      <div className="card p-1.5 mb-3 flex gap-1 items-center overflow-x-auto border-slate-200">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === f.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {f.label}{' '}
            <span className="font-mono text-2xs text-slate-400 tabular-nums">{f.count}</span>
          </button>
        ))}
      </div>

      {/* Line items */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Description</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Amount</th>
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Match</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-xs text-slate-400 italic">
                    No lines match this filter
                  </td>
                </tr>
              ) : filteredLines.map((l: any) => {
                const amt = parseFloat(l.amount);
                const isUnmatched = l.match_status === 'unmatched';
                const isIgnored = l.match_status === 'ignored';
                return (
                  <tr
                    key={l.id}
                    className={clsx(
                      'hover:bg-slate-50/60',
                      isUnmatched && 'bg-amber-50/30',
                      isIgnored && 'opacity-60'
                    )}
                  >
                    <td className="px-3 py-2 font-mono tabular-nums text-slate-600">
                      {new Date(l.txn_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{l.description}</div>
                      {l.reference && (
                        <div className="font-mono text-2xs text-slate-400 mt-0.5">{l.reference}</div>
                      )}
                    </td>
                    <td className={clsx(
                      'px-3 py-2 text-right font-mono font-semibold tabular-nums',
                      amt < 0 ? 'text-danger' : 'text-sage-700'
                    )}>
                      {amt < 0 ? '−' : '+'}${Math.abs(amt).toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <MatchStatusBadge status={l.match_status} />
                      {l.entry_number && (
                        <div className="font-mono text-2xs text-slate-500 mt-0.5">
                          {l.entry_number}
                          {l.match_confidence && (
                            <span className="ml-1 text-slate-400">
                              · {(parseFloat(l.match_confidence) * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!isLocked && isUnmatched && (
                        <div className="flex gap-1 justify-end">
                          <button
                            className="text-2xs font-semibold text-finance-700 hover:bg-finance-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setMatchingLineId(l.id)}
                          >
                            Match
                          </button>
                          <button
                            className="text-2xs font-semibold text-slate-500 hover:bg-slate-100 px-2 py-1 rounded transition-colors"
                            onClick={() => setIgnoringLineId(l.id)}
                          >
                            Ignore
                          </button>
                        </div>
                      )}
                      {!isLocked && ['auto_matched', 'manual_matched'].includes(l.match_status) && (
                        <button
                          className="text-2xs font-semibold text-slate-500 hover:bg-slate-100 px-2 py-1 rounded transition-colors"
                          onClick={() => unmatchMut.mutate(l.id)}
                        >
                          Unmatch
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {matchingLineId && (
        <ManualMatchModal
          statementLineId={matchingLineId}
          onClose={() => setMatchingLineId(null)}
          onMatched={() => {
            setMatchingLineId(null);
            qc.invalidateQueries({ queryKey: ['bank-statement', statementId] });
            onChanged();
          }}
        />
      )}

      {ignoringLineId && (
        <IgnoreDialog
          loading={ignoreMut.isPending}
          onClose={() => setIgnoringLineId(null)}
          onConfirm={(reason) => ignoreMut.mutate({ lineId: ignoringLineId, reason })}
        />
      )}
    </div>
  );
}

function MatchStatusBadge({ status }: { status: string }) {
  const cls = {
    unmatched:       'bg-amber-50 text-amber-800 ring-amber-200',
    auto_matched:    'bg-sage-50 text-sage-700 ring-sage-200',
    manual_matched:  'bg-finance-50 text-finance-800 ring-finance-200',
    ignored:         'bg-slate-100 text-slate-500 ring-slate-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status.replace('_', ' ')}
    </span>
  );
}

function LegendDot({ color, label, count }: { color: string; label: string; count: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={clsx('w-2 h-2 rounded-sm', color)} />
      <span className="uppercase tracking-wider font-semibold">{label}</span>
      <span className="font-mono tabular-nums text-slate-500">{count}</span>
    </div>
  );
}

// ============================================================
// ManualMatchModal — reconciliation workspace
// ============================================================
function ManualMatchModal({
  statementLineId, onClose, onMatched,
}: { statementLineId: string; onClose: () => void; onMatched: () => void }) {
  const q = useQuery({
    queryKey: ['match-candidates', statementLineId],
    queryFn: () => accountingApi.getMatchCandidates(statementLineId),
  });
  const [notes, setNotes] = useState('');

  const matchMut = useMutation({
    mutationFn: (journalLineId: string) =>
      accountingApi.manualMatchLine(statementLineId, journalLineId, notes || undefined),
    onSuccess: () => { toast.success('Matched'); onMatched(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;
  const bankLine = q.data.bankLine;
  const bankAmount = parseFloat(bankLine.amount);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-slate-200">
        <div className="eyebrow text-finance-700 mb-1">Manual match · Treasury</div>
        <h2 className="text-xl font-bold font-display text-slate-900">
          Reconcile bank line
        </h2>

        {/* Bank line anchor */}
        <div className="mt-4 bg-finance-50 rounded-xl p-4 border border-finance-200">
          <div className="eyebrow text-finance-700 mb-1">Bank transaction</div>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-medium text-slate-900 truncate">
                {bankLine.description}
              </div>
              <div className="font-mono text-xs text-slate-500 mt-0.5">
                {new Date(bankLine.txnDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {bankLine.reference && <span className="ml-2">· {bankLine.reference}</span>}
              </div>
            </div>
            <div className={clsx(
              'font-mono font-bold text-xl tabular-nums flex-shrink-0',
              bankAmount < 0 ? 'text-danger' : 'text-sage-700'
            )}>
              {bankAmount < 0 ? '−' : '+'}${Math.abs(bankAmount).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 flex-shrink-0 border-b border-slate-200">
        <label className="label">Match notes <span className="font-normal text-slate-400 normal-case tracking-normal">(optional — stored on audit log)</span></label>
        <input
          className="input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Partial payment for invoice INV-2024-005"
        />
      </div>

      <div className="px-6 py-4 flex-shrink-0">
        <div className="eyebrow text-slate-500">Candidate journal lines</div>
        <div className="text-xs text-slate-500 mt-1">
          Unreconciled GL lines on this account. Look for matching date + amount.
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-6 pb-4">
        {q.data.candidates.length === 0 ? (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <EmptyState
              message="No candidate lines"
              hint="There are no unreconciled GL lines on this account. You may need to post a journal entry first."
            />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden h-full flex flex-col">
            <div className="overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">JE #</th>
                    <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                    <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Description</th>
                    <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Amount</th>
                    <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Signals</th>
                    <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(q.data?.candidates ?? []).map((c: any, i: number) => {
                    const amt = c.netAmount;
                    const perfectMatch = c.amountMatch && c.daysDiff === 0;
                    return (
                      <tr
                        key={c.journalLineId}
                        className={clsx(
                          i % 2 ? 'bg-slate-50/40' : 'bg-white',
                          perfectMatch && 'bg-sage-50/40'
                        )}
                      >
                        <td className="px-3 py-2 font-mono font-semibold text-slate-900">{c.entryNumber}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-slate-600">
                          {new Date(c.entryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-3 py-2 text-slate-700 max-w-xs truncate">{c.description}</td>
                        <td className={clsx(
                          'px-3 py-2 text-right font-mono font-semibold tabular-nums',
                          amt < 0 ? 'text-danger' : 'text-sage-700'
                        )}>
                          {amt < 0 ? '−' : '+'}${Math.abs(amt).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {c.amountMatch ? (
                              <span className="badge bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200 text-2xs">✓ amount</span>
                            ) : (
                              <span className="badge bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 text-2xs">amt diff</span>
                            )}
                            {c.daysDiff === 0 ? (
                              <span className="badge bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200 text-2xs">✓ same day</span>
                            ) : (
                              <span className="badge bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200 text-2xs">
                                {c.daysDiff}d {c.daysDiff < 0 ? 'before' : 'after'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            className={clsx(
                              'text-2xs font-semibold px-3 py-1.5 rounded-pill transition-colors',
                              perfectMatch
                                ? 'bg-sage-600 text-white hover:bg-sage-700'
                                : 'bg-white text-finance-700 ring-1 ring-inset ring-finance-200 hover:bg-finance-50'
                            )}
                            disabled={matchMut.isPending}
                            onClick={() => matchMut.mutate(c.journalLineId)}
                          >
                            {matchMut.isPending ? '…' : 'Match'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex-shrink-0 flex justify-end">
        <button className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// ============================================================
// IgnoreDialog — replaces prompt()
// ============================================================
function IgnoreDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-slate-600 mb-1">Ignore · Treasury</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Ignore bank line</h2>
        <p className="text-sm text-slate-500 mt-2">
          The line stays on the statement for audit trail but doesn't need to match a journal entry. Use for bank fees already booked elsewhere, outbound transfers, or duplicates.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Bank fee posted directly in January"
        />
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Ignoring…' : 'Ignore line'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// ImportModal — CSV paste import
// ============================================================
function ImportModal({ onClose, onImported }: { onClose: () => void; onImported: (id: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [accountId, setAccountId] = useState('');
  const [statementDate, setStatementDate] = useState(today);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountingApi.listAccounts('asset'),
  });

  function parseCsv(text: string): Array<{ txnDate: string; description: string; reference: string; amount: number }> {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return [];
    const hasHeader = /date|descr|amount|ref/i.test(lines[0]);
    const dataLines = hasHeader ? lines.slice(1) : lines;
    return dataLines.map((line, i) => {
      const cells = line.split(',').map((c) => c.trim());
      if (cells.length < 4) throw new Error(`Line ${i + 1}: expected 4 columns, got ${cells.length}`);
      const [date, description, reference, amountStr] = cells;
      const amount = parseFloat(amountStr);
      if (isNaN(amount)) throw new Error(`Line ${i + 1}: "${amountStr}" is not a number`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error(`Line ${i + 1}: date must be YYYY-MM-DD, got "${date}"`);
      return { txnDate: date, description, reference, amount };
    });
  }

  const mut = useMutation({
    mutationFn: () => {
      const lines = parseCsv(csvText);
      return accountingApi.importBankStatement({
        accountId, statementDate, openingBalance, closingBalance,
        fileName: fileName || undefined,
        format: 'csv', lines,
      });
    },
    onSuccess: (d) => { toast.success(`Imported ${d.statementNumber} · ${d.lineCount} lines`); onImported(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  let parseError: string | null = null;
  let parsedCount = 0;
  let parsedSum = 0;
  if (csvText.trim()) {
    try {
      const lines = parseCsv(csvText);
      parsedCount = lines.length;
      parsedSum = lines.reduce((s, l) => s + l.amount, 0);
    } catch (e) {
      parseError = (e as Error).message;
    }
  }
  const computedClosing = openingBalance + parsedSum;
  const balanceMismatch = Math.abs(computedClosing - closingBalance) > 0.01;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow text-finance-700 mb-1">Import · Treasury</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Import bank statement</h2>
        <p className="text-sm text-slate-500 mt-1">
          Paste CSV from your bank's export. Required columns: date, description, reference, amount. Negatives = money out.
        </p>
      </div>

      <div className="px-6 pb-5 flex-1 overflow-y-auto space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Bank account</label>
            <select className="input" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Select asset account…</option>
              {accountsQuery.data?.filter((a: any) => a.code.startsWith('1-10-100'))
                .map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
              {accountsQuery.data?.filter((a: any) => !a.code.startsWith('1-10-100'))
                .map((a: any) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Statement date</label>
            <input type="date" className="input" value={statementDate} onChange={(e) => setStatementDate(e.target.value)} />
          </div>
          <div>
            <label className="label">Opening balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
              <input
                type="number" step={0.01}
                className="input pl-6 tabular-nums"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <div>
            <label className="label">Closing balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
              <input
                type="number" step={0.01}
                className="input pl-6 tabular-nums"
                value={closingBalance}
                onChange={(e) => setClosingBalance(parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="col-span-2">
            <label className="label">File name <span className="font-normal text-slate-400 normal-case tracking-normal">(for audit trail)</span></label>
            <input className="input" value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="e.g. BoA-checking-2026-04.csv" />
          </div>
        </div>

        <div>
          <label className="label">CSV data</label>
          <div className="text-2xs text-slate-500 mb-2 leading-relaxed">
            Columns: <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700">date,description,reference,amount</code>.
            Date format <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700">YYYY-MM-DD</code>. Negative amounts = money out. Header row optional.
          </div>
          <textarea
            className="input font-mono text-xs"
            rows={10}
            placeholder={`2026-04-01,Customer ACME payment,INV-2024,1500.00\n2026-04-02,Vendor XYZ wire,WIRE123,-2500.00\n2026-04-03,Bank fee,FEE,-25.00`}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
        </div>

        {parseError && (
          <div className="bg-danger/10 border border-danger/30 rounded-xl px-3 py-2 text-xs text-danger">
            <span className="font-semibold">Parse error:</span> {parseError}
          </div>
        )}

        {csvText.trim() && !parseError && (
          <div className={clsx(
            'rounded-xl px-3 py-2 text-xs border flex items-start gap-3',
            balanceMismatch
              ? 'bg-amber-50 text-amber-800 border-amber-200'
              : 'bg-sage-50 text-sage-800 border-sage-200'
          )}>
            <div className="flex-1">
              <div className="font-semibold mb-0.5">
                {balanceMismatch ? '⚠ Balance check failed' : '✓ Balance check passed'}
              </div>
              <div className="font-mono tabular-nums">
                {parsedCount} line{parsedCount === 1 ? '' : 's'} · sum ${parsedSum.toFixed(2)} ·
                opening + sum = ${computedClosing.toFixed(2)}
                {balanceMismatch && ` ≠ closing ${fmtCurrency(closingBalance)}`}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex-shrink-0 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!accountId || !!parseError || parsedCount === 0 || balanceMismatch || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Importing…' : 'Import statement'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
